/**
 * @file TitleBadge.tsx
 * @brief Circular abbreviation badge for a courtesy title row.
 *
 * @author DES
 * @date 19-06-2026
 */

import { Type } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import {
  findTitle,
  titleDisplayAbbrev,
  type TitleOption,
} from "@/shared/data/titles";

interface TitleBadgeProps {
  value?: string | null;
  title?: TitleOption | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const WRAP: Record<NonNullable<TitleBadgeProps["size"]>, string> = {
  sm: "h-5 w-5 text-[8px]",
  md: "h-10 w-10 text-[11px]",
  lg: "h-14 w-14 text-sm",
};

const ABBREV_LEN: Record<NonNullable<TitleBadgeProps["size"]>, number> = {
  sm: 2,
  md: 3,
  lg: 4,
};

export function TitleBadge({
  value,
  title,
  size = "md",
  className,
}: TitleBadgeProps) {
  const resolved = title ?? findTitle(value);

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/40 font-semibold leading-none text-brand",
        WRAP[size],
        className,
      )}
    >
      {resolved ? (
        <span aria-hidden>
          {titleDisplayAbbrev(resolved, ABBREV_LEN[size])}
        </span>
      ) : (
        <Type
          className={cn(
            "text-muted-foreground",
            size === "sm" ? "h-3 w-3" : size === "lg" ? "h-7 w-7" : "h-4 w-4",
          )}
          aria-hidden
        />
      )}
    </span>
  );
}
